import {
  faEnvelopeOpenText,
  faFileCircleXmark,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Menu } from 'primereact/menu'
import { MenuItem } from 'primereact/menuitem'
import { Tooltip } from 'primereact/tooltip'
import { classNames } from 'primereact/utils'
import { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { closeMinimizedEmailWindow } from '../../../../../features/projectManagement/projectManagementSlice'
import useToastr from '../../../../../hooks/useToastr'
import ProjectService from '../../../../../services/ProjectService'
import { HandlerAssignmentDraft } from '../../interfaces/pmr-e2e-project-details-orderline-model'
import { useDiscardDraft } from '../handler-assignment-notification-email-editor-dialog/useDiscardDraft'

type Props = {
  id: string
  className: string
  tooltipTarget: string
  onResume: (draft: HandlerAssignmentDraft) => void
}

const MailDraftIcon = ({ className, tooltipTarget, id, onResume }: Props) => {
  const dispatch = useDispatch()
  const menuLeft = useRef<Menu>(null)
  const [discardDraftWarningDialogVisible, setDiscardDraftWarningDialogVisible] =
    useState<boolean>(false)
  const { showInfo } = useToastr()
  const { discardDraft } = useDiscardDraft()

  const handleDiscardMenuItemClick = () => {
    setDiscardDraftWarningDialogVisible(true)
  }

  const onDiscard = () => {
    dispatch(closeMinimizedEmailWindow({ id }))

    discardDraft(id)
    showInfo(
      'Draft Discarded Successfully',
      'The email draft has been discarded successfully. No changes were made to the GMAW Handler.'
    )
  }

  const handleResumeDraftMenuItemClick = async () => {
    const draft = await ProjectService.getHandlerAssignmentMailDraft(id, false)
    onResume(draft.data!)
  }

  const renderMenuItem = (label: string, icon: IconDefinition, onClick: () => void) => (
    <div className="p-menuitem-content" data-pc-section="content">
      <span className="p-menuitem-link" onClick={onClick}>
        <FontAwesomeIcon icon={icon} className="mr-2" />
        {label}
      </span>
    </div>
  )

  const items: MenuItem[] = [
    {
      template: () =>
        renderMenuItem('Resume Draft', faEnvelopeOpenText, handleResumeDraftMenuItemClick),
    },
    {
      template: () =>
        renderMenuItem('Discard Draft', faFileCircleXmark, handleDiscardMenuItemClick),
    },
  ]

  const discardWarningDialogFooter = (
    <div className="text-center">
      <Button
        label="CANCEL"
        className="default-cancel-button"
        onClick={() => setDiscardDraftWarningDialogVisible(false)}
      />
      <Button label="YES" className="default-save-button" onClick={onDiscard} />
    </div>
  )

  return (
    <>
      <Tooltip autoHide={true} position="right" className={className} target={'.' + tooltipTarget}>
        You have an unsaved email draft. Would you like to resume?
      </Tooltip>

      <Menu model={items} popup ref={menuLeft} id="popup_menu_left" />

      <span onClick={e => menuLeft.current?.toggle(e)}>
        <FontAwesomeIcon
          icon={faEnvelopeOpenText}
          className={classNames('ml-2 text-[1.1rem] text-yellow-600 cursor-pointer', tooltipTarget)}
        />
      </span>

      <Dialog
        visible={discardDraftWarningDialogVisible}
        onHide={() => setDiscardDraftWarningDialogVisible(false)}
        header={'Discard Draft'}
        footer={discardWarningDialogFooter}
        className="default"
      >
        <p className="m-0">Are you sure you want to discard this draft?</p>
        <p className="m-0">Any unsaved changes will be permanently lost.</p>
      </Dialog>
    </>
  )
}

export default MailDraftIcon
